import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import innerStyles from './styles/outline-location-card.base.pcss?inline';
import globalStyles from './styles/outline-location-card.global.pcss?inline';
import { SlotHelper } from '../../../controllers/slot-helper';

export const cardAccentVariants = ['primary', 'secondary'] as const;
export type CardAccentVariant = (typeof cardAccentVariants)[number];


/**
 * The OutlineLocationCard component - simplified version of teaser card for locations
 * @element outline-location-card
 */
@customElement('outline-location-card')
export class OutlineLocationCard extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });
  private _slotHelper = new SlotHelper(this);

  @property({type: String, reflect: true})
  accent: CardAccentVariant;

  @property({type: Boolean, attribute: 'has-heading', reflect: true})
  hasHeading: boolean;

  @property({type: Boolean, attribute: 'has-line-1'})
  hasLine1: boolean;

  @property({type: Boolean, attribute: 'has-line-2'})
  hasLine2: boolean;

  @property({type: Boolean, attribute: 'has-city-state-zip'})
  hasCityStateZip: boolean;

  @property({type: Boolean, attribute: 'has-phone'})
  hasPhone: boolean;

  @property({type: Boolean, attribute: 'has-cta'})
  hasCTA: boolean;

  @property({type: Boolean, attribute: 'without-border'})
  withoutBorder: boolean;

  render(): TemplateResult {
    this.hasHeading = this._slotHelper.isNotEmpty('heading');
    this.hasLine1 = this._slotHelper.isNotEmpty('line1');
    this.hasLine2 = this._slotHelper.isNotEmpty('line2');
    this.hasCityStateZip = this._slotHelper.isNotEmpty('city-state-zip');
    this.hasPhone = this._slotHelper.isNotEmpty('phone');
    this.hasCTA = this._slotHelper.isNotEmpty('cta');

    return html`
      <section class="outline-location-card${this.withoutBorder ? ' without-border' : ''}">
        <div class="outline-location-card--main">
          ${this.hasHeading ? html`
          <div class="heading-wrapper">
            <slot name="heading"></slot>
          </div>
          ` : nothing}
          <div class="outline-location-card--location-info">
            <div class="outline-location-card--location-col">
              ${this.addressTemplate()}
              ${this.ctaTemplate()}
            </div>
            <div class="outline-location-card--location-col">
              ${this.phoneTemplate()}
            </div>
          </div>
        </div>
      </section>
    `;
  }

  addressTemplate() {
    if (this.hasLine1 || this.hasLine2 || this.hasCityStateZip) {
      return html `
        <div class="address-wrapper">
          ${this.hasLine1
          ? html`<div class="line-1-wrapper"><slot name="line1"></slot></div>`
          : null
          }
          ${this.hasLine2
          ? html`<div class="line-2-wrapper"><slot name="line2"></slot></div>`
          : null
          }
          ${this.hasCityStateZip
          ? html`<div class="city-state-zip-wrapper"><slot name="city-state-zip"></slot></div>`
          : null
          }
        </div>
        `
    } else return
  }

  phoneTemplate() {
    if (this.hasPhone) {
      return html `<div class="phone-wrapper"><slot name="phone"></slot></div>`
    } else return
  }

  ctaTemplate() {
    if (this.hasCTA) {
      return html `<div class="cta-wrapper"><slot name="cta"></slot></div>`
    } else return
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-location-card': OutlineLocationCard;
  }
}